/**
 * 翻译 —— 普通话 → 方言
 * ============================================================
 * 对应需求文档 D 组。用户在原场景里直接得到方言说法，并能跟着说出来。
 *
 * ── 界面上必须守住的三条 ────────────────────────────────────
 *   1. **标为 AI 翻译，仅供参考** —— 结果由大语言模型生成，未经母语者验证。
 *      方言翻译质量是未知数，不能让用户以为这是权威答案。
 *   2. **显示回译** —— 把方言译文再译回普通话。用户不一定会说这个方言，
 *      回译是他唯一能核对"意思有没有跑偏"的手段。
 *   3. **模型自报不确定时显著提示** —— 不装作很确定。
 *
 * 复用：/api/tts（播放）、/api/recognize（跟读识别）、useRecorder（录音）、
 *       lib/dialects（当前方言）。没有重复实现任何已有能力。
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { getSelectedDialectId, fetchDialect } from '../lib/dialects';
import { useRecorder } from '../hooks/useRecorder';
import { savePhrase, removePhrase, isSaved, getSavedPhrases, putAudio } from '../lib/savedPhrases';
import { recognizeViaRelay } from '../lib/voiceRelay';
import BottomNav from '../components/BottomNav';

/** 场景快捷句：覆盖最常见的外出沟通需求 */
const SCENES = [
  { id: 'buy', label: '买东西', icon: 'shopping_bag', items: ['这个多少钱？', '能便宜点吗？', '可以刷卡吗？'] },
  { id: 'eat', label: '吃饭点单', icon: 'restaurant', items: ['我要一份这个。', '不要辣。', '买单。'] },
  { id: 'ask', label: '问路', icon: 'directions', items: ['请问怎么走？', '离这里远吗？', '我要去火车站。'] },
  { id: 'help', label: '求助', icon: 'help_outline', items: ['我听不懂。', '请说慢一点。', '能帮我一下吗？'] },
];

export default function TranslatePage() {
  const [dialect, setDialect] = useState(null);
  const [input, setInput] = useState('');
  const [scene, setScene] = useState(null);

  const [result, setResult] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);

  // 播放
  const [playing, setPlaying] = useState(false);
  const [playError, setPlayError] = useState(null);
  const [spoken, setSpoken] = useState(null); // 当前播放的是哪一条文本

  // 跟读
  const [heard, setHeard] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);

  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const recorder = useRecorder();

  // 收藏
  const [savedFlag, setSavedFlag] = useState(false);
  const [savedCount, setSavedCount] = useState(() => getSavedPhrases().length);
  const [saveMsg, setSaveMsg] = useState(null);
  // 暂存刚播过的音频 blob：用户点收藏时直接写入离线缓存，不用再播一次
  const blobRef = useRef(null);

  useEffect(() => {
    fetchDialect(getSelectedDialectId())
      .then(setDialect)
      .catch(() => {});
  }, []);

  // 卸载时释放音频
  useEffect(
    () => () => {
      try {
        audioRef.current?.pause();
      } catch {
        /* 忽略 */
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  /* ---------- 翻译 ---------- */
  const translate = useCallback(
    async (text) => {
      const t = String(text ?? input).trim();
      if (!t) {
        setError('请输入要翻译的内容');
        return;
      }
      setTranslating(true);
      setError(null);
      setResult(null);
      setHeard(null);
      setCheckError(null);
      recorder.resetRecording();
      try {
        const res = await api.post('/api/translate', {
          text: t,
          dialect: getSelectedDialectId(),
        });
        const data = res.data?.data || res.data;
        setResult(data);
        // 换了一句就要重算收藏状态，否则按钮会停留在上一句的状态
        setSavedFlag(
          isSaved(data?.dialect || getSelectedDialectId(), data?.dialectText || '')
        );
        setSaveMsg(null);
      } catch (e) {
        setError(e.response?.data?.error || e.message || '翻译失败');
      } finally {
        setTranslating(false);
      }
    },
    [input, recorder]
  );

  /* ---------- 播放 ---------- */
  const play = useCallback(
    async (text, key) => {
      if (!text) return;
      try {
        audioRef.current?.pause();
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);

        setPlaying(true);
        setSpoken(key);
        setPlayError(null);

        const resp = await api.post(
          '/api/tts',
          { text, language: 'cantonese', dialect: getSelectedDialectId() },
          { responseType: 'blob' }
        );
        // 暂存 blob：收藏时写入离线缓存，实现「收藏即离线可用」
        blobRef.current = resp.data;
        const url = URL.createObjectURL(resp.data);
        urlRef.current = url;

        if (!audioRef.current) audioRef.current = new Audio();
        const a = audioRef.current;
        a.src = url;
        a.onended = () => {
          setPlaying(false);
          setSpoken(null);
        };
        a.onerror = () => {
          setPlaying(false);
          setSpoken(null);
          setPlayError('播放失败，可能是音频格式不被支持');
        };
        await a.play().catch((err) => {
          setPlaying(false);
          setSpoken(null);
          // 自动播放被拦截时明确告知，不静默失败
          setPlayError(
            err?.name === 'NotAllowedError'
              ? '浏览器拦截了播放，请再点一次'
              : '播放失败：' + (err?.message || '未知原因')
          );
        });
      } catch (e) {
        setPlaying(false);
        setSpoken(null);
        setPlayError(e.message || '语音合成失败');
      }
    },
    []
  );

  /* ---------- 收藏 ---------- */
  const toggleSave = useCallback(async () => {
    if (!result?.dialectText) return;
    const dialectId = result.dialect || getSelectedDialectId();

    if (savedFlag) {
      removePhrase(`${dialectId}::${result.dialectText.trim()}`);
      setSavedFlag(false);
      setSavedCount(getSavedPhrases().length);
      setSaveMsg('已取消收藏');
      return;
    }

    const r = savePhrase({
      dialectId,
      dialectName: result.dialectName,
      dialectText: result.dialectText,
      romanization: result.romanization,
      source: result.source,
      backTranslation: result.backTranslation,
      notes: result.notes,
      uncertain: result.uncertain,
    });
    if (!r.ok) {
      setSaveMsg(r.error || '收藏失败');
      return;
    }
    setSavedFlag(true);
    setSavedCount(getSavedPhrases().length);

    // 顺手把刚播过的音频写进离线缓存 → 收藏后没网也能听
    if (r.id && blobRef.current) {
      const ok = await putAudio(r.id, blobRef.current);
      setSaveMsg(ok ? '已收藏（音频已存本地，可离线播放）' : '已收藏（音频未缓存，需联网播放）');
    } else {
      setSaveMsg(
        r.duplicate ? '之前已收藏过这句' : '已收藏（点一次发音即可离线播放）'
      );
    }
  }, [result, savedFlag]);

  /* ---------- 跟读：录音 → 识别 → 与译文对比 ---------- */
  const checkReading = useCallback(async () => {
    if (!result?.dialectText || checking) return;
    const file = await recorder.getAudioFile();
    if (!file) {
      setCheckError('没有取到录音数据，请重录');
      return;
    }
    setChecking(true);
    setCheckError(null);
    try {
      /*
       * 先试中国侧中继，失败再走主服务（行为与改动前一致）。
       * 中继省掉的是"手机→美东→杭州→美东"这两趟跨太平洋。
       */
      const dialect = getSelectedDialectId();
      const relayed = await recognizeViaRelay(file, dialect);
      if (relayed) {
        setHeard({ text: relayed.text, provider: relayed.provider });
      } else {
        const fd = new FormData();
        fd.append('audio', file);
        fd.append('dialect', dialect);
        const res = await api.post('/api/recognize', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        setHeard(res.data?.data || res.data);
      }
    } catch (e) {
      setCheckError(e.response?.data?.error || e.message || '识别失败');
    } finally {
      setChecking(false);
    }
  }, [result, checking, recorder]);

  const reset = () => {
    recorder.resetRecording();
    setHeard(null);
    setCheckError(null);
  };

  const target = result?.dialectText || '';
  const diff = target
    ? [...target].filter((c) => /\S/.test(c)).map((c) => ({ ch: c, hit: (heard?.text || '').includes(c) }))
    : [];

  return (
    <div className="min-h-screen bg-[#fdfaf1] pb-28 dark:bg-zinc-900">
      <header className="bg-gradient-to-br from-teal-500 to-sky-500 px-6 pb-6 pt-12 dark:from-teal-700 dark:to-sky-700">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">翻译</h1>
            <p className="mt-1 text-sm text-white/80">
              普通话 → {dialect?.displayName || '方言'}
            </p>
          </div>
          {/* 收藏夹入口放这里而不是导航栏：一级导航已有 5 项，再加会挤 */}
          <div className="flex items-center gap-1.5">
            <Link
              to="/conversation"
              className="flex items-center gap-1 rounded-full bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm active:scale-95"
            >
              <span className="material-icons !text-sm">forum</span>
              对话
            </Link>
            <Link
              to="/saved"
              className="flex items-center gap-1 rounded-full bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm active:scale-95"
            >
              <span className="material-icons !text-sm">bookmark</span>
              收藏 {savedCount > 0 ? savedCount : ''}
            </Link>
          </div>
        </div>
      </header>

      <main className="space-y-4 px-5 py-5">
        {/* 场景快捷 */}
        <section>
          <p className="mb-2 text-xs font-bold text-zinc-500">选个场景</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {SCENES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setScene(scene?.id === s.id ? null : s)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition ${
                  scene?.id === s.id
                    ? 'bg-teal-500 text-white'
                    : 'bg-white text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
                }`}
              >
                <span className="material-icons !text-sm">{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
          {scene && (
            <div className="mt-2 space-y-1.5">
              {scene.items.map((it) => (
                <button
                  key={it}
                  type="button"
                  onClick={() => {
                    setInput(it);
                    translate(it);
                  }}
                  className="w-full rounded-xl bg-white px-4 py-2.5 text-left text-sm text-zinc-700 active:scale-[0.99] dark:bg-zinc-800 dark:text-zinc-200"
                >
                  {it}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* 输入 */}
        <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <label className="mb-1.5 block text-xs font-bold text-zinc-500">要说的话（普通话）</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder="例如：这个多少钱？能便宜点吗？"
            className="w-full resize-none rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-sm text-zinc-800 outline-none focus:border-teal-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          />
          <div className="mt-1 flex justify-between text-[10px] text-zinc-400">
            <span>一次最多 200 字，长句建议分句翻译</span>
            <span>{input.length}/200</span>
          </div>
          <button
            type="button"
            onClick={() => translate()}
            disabled={translating || !input.trim()}
            className="mt-3 w-full rounded-xl bg-teal-500 py-3 text-sm font-bold text-white transition active:scale-[0.99] disabled:opacity-40"
          >
            {translating ? '翻译中…' : `翻译成${dialect?.name || '方言'}`}
          </button>
          {error && (
            <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
              {error}
            </p>
          )}
        </section>

        {/* 结果 */}
        {result && (
          <>
            <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                  {result.dialectName}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={toggleSave}
                    className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold transition ${
                      savedFlag
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                        : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300'
                    }`}
                  >
                    <span className="material-icons !text-sm">
                      {savedFlag ? 'bookmark' : 'bookmark_border'}
                    </span>
                    {savedFlag ? '已收藏' : '收藏'}
                  </button>
                  <button
                    type="button"
                    onClick={() => play(result.dialectText, 'main')}
                    disabled={playing && spoken === 'main'}
                    className="flex items-center gap-1 rounded-full bg-teal-500 px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-60"
                  >
                    <span className="material-icons !text-sm">
                      {playing && spoken === 'main' ? 'volume_up' : 'play_arrow'}
                    </span>
                    {playing && spoken === 'main' ? '播放中' : '听发音'}
                  </button>
                </div>
              </div>

              <p className="text-xl font-bold leading-snug text-zinc-800 dark:text-zinc-100">
                {result.dialectText}
              </p>
              {result.romanization && (
                <p className="mt-1 font-mono text-xs text-teal-600 dark:text-teal-400">
                  {result.romanization}
                </p>
              )}

              {/* 换说法 */}
              {result.alternatives?.length > 0 && (
                <div className="mt-3 border-t border-zinc-100 pt-2.5 dark:border-zinc-700">
                  <p className="mb-1.5 text-[10px] text-zinc-400">也可以说</p>
                  <div className="space-y-1">
                    {result.alternatives.map((a) => (
                      <div key={a} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 text-xs text-zinc-600 dark:text-zinc-300">{a}</span>
                        <button
                          type="button"
                          onClick={() => play(a, a)}
                          disabled={playing && spoken === a}
                          className="shrink-0 text-teal-500 disabled:opacity-50"
                          aria-label={`播放 ${a}`}
                        >
                          <span className="material-icons !text-base">volume_up</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {saveMsg && (
                <p className="mt-2 rounded-lg bg-teal-50 px-2.5 py-1.5 text-[11px] text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                  {saveMsg}
                </p>
              )}

              {playError && (
                <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  {playError}
                </p>
              )}
            </section>

            {/* 回译 —— 用户核对意思的主要依据 */}
            <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
              <p className="mb-1.5 text-[10px] font-bold text-zinc-400">这句话的意思（回译）</p>
              <p className="text-sm text-zinc-700 dark:text-zinc-200">
                {result.backTranslation || '（模型没有给出回译）'}
              </p>
              {result.notes && (
                <p className="mt-2.5 border-t border-zinc-100 pt-2.5 text-[11px] leading-relaxed text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                  <span className="font-bold">用词说明：</span>
                  {result.notes}
                </p>
              )}
            </section>

            {/* 不确定性 + 来源标注 —— 不得省略 */}
            <section
              className={`rounded-2xl p-3.5 ${
                result.uncertain
                  ? 'bg-amber-50 dark:bg-amber-900/30'
                  : 'bg-zinc-100 dark:bg-zinc-800'
              }`}
            >
              {result.uncertain && (
                <p className="mb-1.5 text-[11px] font-bold text-amber-800 dark:text-amber-200">
                  ⚠️ 模型对这个说法没有把握，建议找母语者确认后再用
                </p>
              )}
              <p className="text-[10px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                <span className="font-bold">AI 翻译，仅供参考。</span>
                由{result.engine}生成，<span className="font-bold">未经母语者验证</span>。
                方言缺少统一书面标准，同一个意思各地说法可能不同。
                {result.elapsedMs ? ` 耗时 ${(result.elapsedMs / 1000).toFixed(1)} 秒。` : ''}
              </p>
            </section>

            {/* 跟着说 */}
            <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
              <p className="mb-2 text-xs font-bold text-zinc-500">跟着说一遍</p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (recorder.isRecording) {
                      recorder.stopRecording();
                    } else {
                      reset(); // 先清掉上一次的结果，避免旧识别文本和新录音混在一起
                      recorder.startRecording();
                    }
                  }}
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white transition active:scale-95 ${
                    recorder.isRecording ? 'bg-rose-500' : 'bg-teal-500'
                  }`}
                  aria-label={recorder.isRecording ? '停止录音' : '开始录音'}
                >
                  <span className="material-icons !text-2xl">
                    {recorder.isRecording ? 'stop' : 'mic'}
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  {!recorder.audioUrl ? (
                    <p className="text-[11px] leading-relaxed text-zinc-400">
                      {recorder.isRecording
                        ? `录音中 ${recorder.duration}s，说完点方块`
                        : '点麦克风把上面的方言说一遍'}
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={checkReading}
                        disabled={checking}
                        className="flex-1 rounded-xl bg-teal-500 py-2.5 text-xs font-bold text-white disabled:opacity-50"
                      >
                        {checking ? '识别中…' : '提交'}
                      </button>
                      <button
                        type="button"
                        onClick={reset}
                        disabled={checking}
                        className="rounded-xl bg-zinc-100 px-3 py-2.5 text-xs font-bold text-zinc-600 disabled:opacity-50 dark:bg-zinc-700 dark:text-zinc-300"
                      >
                        重录
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {checkError && (
                <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
                  {checkError}
                </p>
              )}

              {heard && (
                <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-700">
                  <p className="text-[10px] text-zinc-400">识别到你说的</p>
                  <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-200">
                    {heard.text || '（没有识别到内容）'}
                  </p>

                  {diff.length > 0 && (
                    <>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {diff.map((d, i) => (
                          <span
                            key={`${d.ch}-${i}`}
                            className={`flex h-7 min-w-7 items-center justify-center rounded-lg px-1.5 text-sm font-bold ${
                              d.hit
                                ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                : 'bg-rose-50 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'
                            }`}
                          >
                            {d.ch}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-[10px] leading-relaxed text-zinc-400">
                        <span className="font-bold">这不是发音评分。</span>
                        只表示这些字有没有出现在识别结果里。方言识别常把方言用字
                        转成同音的普通话字，字对不上不代表你读错了。
                      </p>
                    </>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
